import { Controller } from "@hotwired/stimulus";
import { gsap } from "gsap";
import { whenStageActive } from "./utils/when_stage_active";

// Plays a fade-in + slight lift entrance when the
// closest audit stage becomes active (or a fade-out
// when direction is "out", used to hide a skeleton
// overlay once the real content's reveal delay hits).
//
// Honors prefers-reduced-motion: in reduced mode the
// element is set to its final state immediately,
// no tween.
//
// Why deferred play: the audit show page renders all
// stages up front (hidden by CSS). Without
// `whenStageActive`, the entrance would tween while
// the stage is `display: none`, finishing before the
// user ever sees it.
//
// Values:
// - duration (Number, default 0.5): seconds.
// - delay (Number, default 0): seconds.
// - y (Number, default 8): pixels lifted from.
// - ease (String, default "power2.out"): GSAP ease.
// - direction (String, default "in"): "in" or "out".
//   "in" = fade from 0 to 1 (entrance).
//   "out" = fade from 1 to 0 (exit) and hide
//   the element so it stops capturing pointer
//   events.
export default class extends Controller {
  static values = {
    // Slightly longer than the original 0.5s default:
    // on fast hardware with cascades around 0.18s
    // per chip the effect was over before the eye
    // could register the stagger. 0.7s reads as
    // intentional motion.
    duration: { type: Number, default: 0.7 },
    delay: { type: Number, default: 0 },
    y: { type: Number, default: 8 },
    ease: { type: String, default: "power2.out" },
    direction: { type: String, default: "in" },
  };

  connect() {
    // Pre-set initial visual state so the element
    // doesn't flash its final state during the wait
    // for the stage to become active.
    const out = this.directionValue === "out";
    if (!out) {
      gsap.set(this.element, {
        autoAlpha: 0,
        y: this.yValue,
      });
    }

    this.cancelWait = whenStageActive(
      this.element,
      () => this.play()
    );
  }

  disconnect() {
    this.cancelWait?.();
    this.tween?.kill();
  }

  play() {
    const out = this.directionValue === "out";
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduced) {
      const finalAlpha = out ? 0 : 1;
      gsap.set(this.element, {
        autoAlpha: finalAlpha,
        y: 0,
      });
      return;
    }

    if (out) {
      this.tween = gsap.to(this.element, {
        autoAlpha: 0,
        duration: this.durationValue,
        delay: this.delayValue,
        ease: this.easeValue,
      });
    } else {
      this.tween = gsap.fromTo(
        this.element,
        { autoAlpha: 0, y: this.yValue },
        {
          autoAlpha: 1,
          y: 0,
          duration: this.durationValue,
          delay: this.delayValue,
          ease: this.easeValue,
        }
      );
    }
  }
}
